import { INCOMPLETE_STEP_ERROR } from './formula-engine.service';
import { CORE_FORMULA_VARIABLES, FormulaVariable, RESERVED_NAMES_SHOWN } from './formula.types';

/**
 * DISPLAY-time translation for the calculation feature (card 27 wording pass).
 *
 * The engine's messages ('Unknown variable X', INCOMPLETE_STEP_ERROR, 'Division by Zero'…) are
 * compared in code and match the backend evaluator word for word, so they are NEVER changed or
 * translated at the source. Every screen that shows one to a person passes it through
 * `formulaMessageText` instead. Same for the core variable labels: the ids and the English labels
 * (server list + CORE_FORMULA_VARIABLES) stay as they are; `formulaVariableLabel` translates them
 * when they are shown.
 *
 * A message or label with no mapping, or whose key has no translation, is shown as it came.
 */
export type FormulaTranslate = (key: string, params?: Record<string, unknown>) => string;

/** A COMMON.* key per core variable id. */
export const CORE_FORMULA_VARIABLE_LABEL_KEYS: Readonly<Record<string, string>> = {
  'Item.Quantity': 'COMMON.Formula var quantity',
  'Item.Total': 'COMMON.Formula var line total',
  'Item.Unit_Price': 'COMMON.Formula var unit price',
  'Item.Price_A': 'COMMON.Formula var price a',
  'Item.Price_B': 'COMMON.Formula var price b',
  'Item.Price_C': 'COMMON.Formula var price c',
  'Item.Price_D': 'COMMON.Formula var price d',
  'Item.Discount_Percent': 'COMMON.Formula var discount',
};

/** Engine (frontend + backend) and validate-endpoint messages, exact text -> COMMON.* key. */
const EXACT_MESSAGES: Readonly<Record<string, string>> = {
  // engine (formula-engine.service.ts here and in shared-services)
  'Formula is required': 'COMMON.Calc error empty',
  'Unsupported formula logic_mode': 'COMMON.Calc error unreadable',
  'Formula has no steps': 'COMMON.Calc error no steps',
  'Formula execution failed': 'COMMON.Calc error failed',
  [INCOMPLETE_STEP_ERROR]: 'COMMON.Calc error incomplete step',
  'Result is not a finite number': 'COMMON.Calc error not finite',
  'Division by Zero': 'COMMON.Cannot divide by zero',
  'Incomplete conditional logic': 'COMMON.Calc error incomplete rule',
  'Result is negative': 'COMMON.Calc warning negative',
  // formula-context.service.ts
  'Invalid formula JSON': 'COMMON.Calc error damaged',
  // server structure checks (shared-services validateFormulaStructure)
  'formula_json is required': 'COMMON.Calc error empty',
  'At least one recipe step is required': 'COMMON.Calc error no steps',
  'The first step must be the source value': 'COMMON.Calc error first step',
  'A source step with a value is required': 'COMMON.Formula source required',
  'Operation steps require an operand': 'COMMON.Formula operand required',
  'Conditional formulas require a complete IF condition': 'COMMON.Calc error finish if',
  'Conditional formulas require a right-hand value': 'COMMON.Conditional right required',
  'Conditional formulas require a THEN value': 'COMMON.Conditional then required',
  'Conditional formulas require an ELSE value': 'COMMON.Conditional else required',
  'Discount cannot exceed 100%': 'COMMON.Discount cannot exceed 100%',
  'Percentages must be from 0 to 100': 'COMMON.Enter 0 to 100',
  // server field rules (shared-services field-rules.ts)
  'Field name is required': 'COMMON.Calculation name required',
  'Field names start with a letter and use letters, numbers, spaces, - _ ( ), with no repeated symbols.':
    'COMMON.Field name characters',
  'Use a short unit, up to 12 characters, starting with a letter or symbol (e.g. kg, %, $/hr, m²).':
    'COMMON.Unit suffix invalid',
  'Execution order must be a whole number from 0 to 99.': 'COMMON.Execution order whole number',
};

/** Messages that carry a value after a fixed prefix. The value is internal (an operator code). */
const PREFIX_MESSAGES: ReadonlyArray<readonly [string, string]> = [
  ['Unknown operator: ', 'COMMON.Calc error unknown operator'],
  ['Invalid operation operator: ', 'COMMON.Calc error unknown operator'],
  ['Unknown comparison operator: ', 'COMMON.Calc error unknown comparison'],
  ['Invalid comparison operator: ', 'COMMON.Calc error unknown comparison'],
  ['Invalid formatting operator: ', 'COMMON.Calc error unknown rounding'],
  ['Unknown step type: ', 'COMMON.Calc error unknown step'],
];

const UNKNOWN_VARIABLE_PREFIX = 'Unknown variable ';
const RESERVED_NAME_MESSAGE = /^"(.*)" can't be used as a field name because quotes already use it\./;

/** t(key) when the key has a translation, else null (ngx-translate returns the key itself). */
function translated(t: FormulaTranslate | undefined, key: string, params?: Record<string, unknown>): string | null {
  if (!t) {
    return null;
  }
  const out = t(key, params);
  return typeof out === 'string' && out !== '' && out !== key ? out : null;
}

/**
 * The name a person reads for a variable id: the translated core label, else the label in
 * `variables`, else the core English label, else the custom field's name ("Item.Custom.Width" ->
 * "Width"), else the id.
 */
export function formulaVariableLabel(
  id: string,
  variables?: readonly FormulaVariable[] | null,
  t?: FormulaTranslate,
): string {
  const token = String(id ?? '');
  const key = CORE_FORMULA_VARIABLE_LABEL_KEYS[token];
  const fromKey = key ? translated(t, key) : null;
  if (fromKey) {
    return fromKey;
  }
  const listed = (variables || []).find((v) => v?.id === token)?.label;
  if (listed) {
    return listed;
  }
  const core = CORE_FORMULA_VARIABLES.find((v) => v.id === token)?.label;
  if (core) {
    return core;
  }
  if (token.startsWith('Item.Custom.')) {
    return token.slice('Item.Custom.'.length) || token;
  }
  return token;
}

/**
 * An engine / server message in the reader's language. Unknown messages come back unchanged.
 * `labelFor` names the variables of "Unknown variable X" (defaults to formulaVariableLabel).
 */
export function formulaMessageText(
  message: unknown,
  t: FormulaTranslate,
  labelFor?: (id: string) => string,
): string {
  const raw = message === undefined || message === null ? '' : String(message);
  if (!raw) {
    return raw;
  }
  const exactKey = EXACT_MESSAGES[raw];
  if (exactKey) {
    return translated(t, exactKey) ?? raw;
  }
  if (raw.startsWith(UNKNOWN_VARIABLE_PREFIX)) {
    const name = labelFor || ((id: string) => formulaVariableLabel(id, null, t));
    const names = raw
      .slice(UNKNOWN_VARIABLE_PREFIX.length)
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean)
      .map((id) => name(id))
      .join(', ');
    return translated(t, 'COMMON.Calc error unknown variable', { names }) ?? raw;
  }
  for (const [prefix, key] of PREFIX_MESSAGES) {
    if (raw.startsWith(prefix)) {
      return translated(t, key) ?? raw;
    }
  }
  const reserved = RESERVED_NAME_MESSAGE.exec(raw);
  if (reserved) {
    return (
      translated(t, 'COMMON.Formula field name reserved named', {
        name: reserved[1],
        list: RESERVED_NAMES_SHOWN.join(', '),
      }) ?? raw
    );
  }
  return raw;
}
